import { useEffect, useMemo, useRef, useState } from "react";
import SideNavLayout from "../customsidenav/sidenavlayout";
import dayjs from "dayjs";
import NavigationMenu from "./navigationmenu";
import api from "../api/api";
import CardView from "./cards/cardview";
import ExpandedView from "./cards/expandedcard";
import { Backdrop, CircularProgress } from "@mui/material";
import PcardPopup from "./modals/pcard";
import StatusNotesPopUp from "./modals/statusnotes";
import ModalOverlay from "./modals/pmodal";
import useStore from "../store/zustandstore";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { use } from "react";
import Dashfilter from "./modals/Dashfilter";
import { objs } from "./junk";
import ImagingModal from "./modals/imagingmodal";
import PcardPopupNew from "./modals/pacrdnew";
import DashboardFilterModal from "./modals/dashboardfilters";
import ToastMessage from "../utils/mui/righttoptoast";
import ChatWrapper from "../header/chat/chatwrapper";
import CreateAppointment from "../schedule/modals/createAppointment";
import ModalSwitch from "../schedule/modals/modalswitch";
import { eventBus } from "../utils/eventBus";
import { setTab } from "../reduxtoolkit/filterslice";
import {
  inHouseSummaryToast,
  inHouseTasksToUpload,
  sendInHouseResults,
} from "../tasks/inhouseTestUpload";
import ProviderFilter from "./ProviderFilter";
import "./providerfilter.css";
import { DASHBOARD_TABS, dashboardTabLabel } from "./dashboardTabs";
import {
  EMPTY_PROVIDER_DIRECTORY,
  loadProviderDirectory,
  migrateProviderSelection,
  providerFilterModel,
} from "../utils/providerDirectory";
import { workingLocation } from "../utils/workingLocation";
import { useNow } from "./cards/visittimeline";
import { visitModel, clinicTodayKey } from "./cards/visitflow";
import {
  classifyVisit,
  findPcardRow,
  needsChartResolution,
  switchedVisitMessage,
  visitLabel,
} from "../utils/visitGuard";
import { resolveChartVisit } from "../utils/chartVisitResolver";
import { OPEN_CHART_VISIT, openChartVisitCard } from "../utils/chartVisitSwitch";
import { getPracticeId, practiceFeatures, practiceProviderAliases, practiceShortName } from "../config/practice";
import {
  dashboardSearchKey,
  loadSearchChip,
  readDashboardSearch,
  saveSearchChip,
  stripDashboardSearch,
  withDashboardSearch,
} from "./dashboardSearch";

/* Silent background refresh of today's dashboard (see CareLanding). The v1
   dashboard endpoint is heavy, so: every 2 min at most, plus on focus / tab
   visible (debounced, never within 30 s of the last dashboard fetch). */
const BG_REFRESH_INTERVAL_MS = 120000;
const BG_REFRESH_MIN_GAP_MS = 30000;
const BG_REFRESH_DEBOUNCE_MS = 1000;
/* Anything open over the list: MUI modals/menus, the dashboard's own tailwind
   overlays (fixed inset-0) and ARIA dialogs. A refresh then waits. */
const BG_REFRESH_OVERLAY_SELECTOR =
  '[aria-modal="true"], [role="dialog"], .MuiModal-root:not(.MuiModal-hidden), .fixed.inset-0';

/* "N to check out": loaded cards whose visit is in the "Check out?" reminder
   state, from the same model and shared clock as the cards (so only today's
   visits can count). Clicking narrows the list to them; hidden at zero unless
   the filter is on, so it can be switched off. */
const CheckoutDueChip = ({ rows, active, onToggle }) => {
  const now = useNow();
  const count = useMemo(
    () =>
      rows.reduce(
        (n, row) => (visitModel(row, now)?.checkoutDue ? n + 1 : n),
        0,
      ),
    [rows, now],
  );
  if (!count && !active) return null;
  return (
    <button
      type="button"
      className={`checkout-due${active ? " active" : ""}`}
      aria-pressed={active}
      onClick={onToggle}
      title={
        active
          ? "Show all patients"
          : "Show only patients ready to be checked out"
      }
    >
      {count} to check out
      {active && (
        <span className="checkout-due-x" aria-hidden="true">
          ×
        </span>
      )}
    </button>
  );
};

const formatDate = (dateString) => {
  return dayjs(dateString).format("YYYY-MM-DD");
};

// "YYYY-MM-DD" of a dashboard date (currentDate / appointmentdate strings).
const dayKey = (d) => String(d || "").slice(0, 10);

/* Whole-day provider set for the provider filter: re-fetched on a date change
   and then at most every 10 min from the background tick (the v1 dashboard
   query is heavy; it has stalled under polling before). */
const DAY_ROWS_REFRESH_MS = 600000;
// Dashboard "All" tab: every row of the day whatever its status (gateway
// DASHFILTER_ALL), a superset of the no-tab view.
const DASHFILTER_ALL = 5;

// A dashboard request whose rows cover the whole day: no status tab or the
// All tab, and no patient search, label filter or cross-location search.
const isWholeDayPayload = (p) =>
  (!p?.dashfilter || Number(p.dashfilter) === DASHFILTER_ALL) &&
  !p?.searchflag &&
  !(Array.isArray(p?.filterarr) && p.filterarr.length) &&
  !p?.alldepts;

const CareLanding = () => {
  const currentDate = useSelector((state) => state.filter.currentDate);
  const tab = useSelector((state) => state.filter.tab);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const [imagingmodal, setImagingModal] = useState(false);
  // Params of the most recent dashboard fetch, replayed by refreshDashboard.
  const lastFetchParamsRef = useRef(null);
  // Sequence of list requests (fetchDashboardData): only the newest one's
  // answer is shown, so a slow answer for an earlier patient can never land
  // under a later search's chip.
  const dashFetchSeqRef = useRef(0);
  // Sequence of searches (picked, cleared or restored from the URL): a URL
  // restore's chip lookup applies only while it is still the latest.
  const searchSeqRef = useRef(0);
  // A list load ran since the scroll to restore was set (see pendingScrollRef).
  const sawLoadingRef = useRef(false);
  // Background refresh bookkeeping: normal dashboard fetches in flight, the
  // silent one in flight, when the last one finished, and unmount.
  const dashFetchesInFlightRef = useRef(0);
  const bgRefreshInFlightRef = useRef(false);
  const lastDashFetchAtRef = useRef(0);
  const mountedRef = useRef(true);
  const silentRefreshRef = useRef(null);
  // One-shot guard for the low-census auto-relax of the default Active tab.
  const autoRelaxedActiveRef = useRef(false);
  const [activeTab, setActiveTab] = useState(null);
  const [startdate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isloader, setIsLoader] = useState(false);
  const [dbscount, setDbCount] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filtermodal, setFiltermodal] = useState(false);
  const [selectedFilterName, setSelectedFilterName] = useState();
  const [appliedFilter, setAppliedFilter] = useState();
  const [fetchedData, setFetchedData] = useState([]);
  // Quick provider filter chips (ported from the old care instance,
  // 2026-08-20): client-side filter over the loaded rows. Since 2026-10-08
  // each row is filtered by its provider ID (utils/providerDirectory
  // providerFilterKey: a staff reassignment's provider, else the booked
  // providerid, else a name bucket), never by a name string, so a provider
  // stored under several names ("Reshmi Adupa, MD" / "Reshmi Adupa") is one
  // entry and selecting it shows all their visits; `providerFilter` holds
  // those keys ("id:20"). The options cover the whole day, not just the
  // loaded status tab: dayRows holds the day's tab-less rows (see
  // syncDayRows), and the loaded rows are merged in so a just-reassigned
  // visit's provider is always selectable.
  const [providerFilter, setProviderFilter] = useState([]);
  // provider_master names, loaded once per page load (effect below practiseid).
  const [providerDirectory, setProviderDirectory] = useState(
    EMPTY_PROVIDER_DIRECTORY,
  );
  // The header's working location ("1" / "2" / "all", utils/workingLocation).
  // The server scopes every dashboard call to it, so the whole-day rows are
  // kept per day AND location: a switch made in place (during a recording)
  // must not leave the other location's providers in the options.
  // Read straight from the store here: userConfig is declared further down.
  const workingLoc = useSelector((state) => workingLocation(state.userconfig?.config));
  const workingLocRef = useRef(workingLoc);
  workingLocRef.current = workingLoc;
  const [dayRows, setDayRows] = useState({ date: "", location: "", rows: [] });
  // { value, label } per provider: value = the provider-id key (what the
  // filter matches), label = one display name per id ("Dr. Malik"). One
  // entry per provider id, from the loaded day of the current location only:
  // no name is ever hardcoded here. keyOf comes from the same rows, so the
  // options and the filtering always agree.
  const providerModel = useMemo(() => {
    const day =
      dayRows.date === dayKey(currentDate) && dayRows.location === workingLoc
        ? dayRows.rows
        : [];
    return providerFilterModel([...day, ...(fetchedData || [])], providerDirectory, {
      // "Dr. Malik" labels on 9928; other practices show the provider_master name.
      doctorLabels: practiceFeatures().providerFilterLabels,
      // one person under two Athena ids (9928: 16 -> 15); {} elsewhere
      aliases: practiceProviderAliases(),
    });
  }, [dayRows, currentDate, workingLoc, fetchedData, providerDirectory]);
  const providerOptions = providerModel.options;
  const providerOptionLabel = (value) =>
    (providerOptions.find((o) => o.value === value) || {}).label || value;
  const providerFilteredData = useMemo(() => {
    if (providerFilter.length === 0) return fetchedData || [];
    return (fetchedData || []).filter((x) =>
      providerFilter.includes(providerModel.keyOf(x)),
    );
  }, [fetchedData, providerFilter, providerModel]);
  useEffect(() => {
    // Keep the selection on the current options: a provider NAME from before
    // the id keys maps to its id's entry, and a provider no longer in the
    // loaded day is dropped, rather than clearing the whole selection.
    const next = migrateProviderSelection(providerFilter, providerOptions);
    if (next !== providerFilter) setProviderFilter(next);
  }, [providerOptions, providerFilter]);
  const [searchdate, setSearchDate] = useState(formatDate(new Date()));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStatusNotesPopUp, setIsStatusNotesPopUp] = useState(false);
  const [isPcard, setIsPcard] = useState(false);
  const [isCardView, setIsCardView] = useState(true);
  const [selectedCard, setSelectedCard] = useState({});
  const [signInPatient, setSignInPatient] = useState(state?.item || null);
  const [searchedUser, setSearchedUser] = useState(null);
  // Toolbar "N to check out" filter (client-side over the loaded day), list
  // view only: tiles have no reminder. The shared visit clock only re-renders
  // this page while the filter is on. Declared below isCardView and
  // searchedUser, which it reads during render.
  const [checkoutOnly, setCheckoutOnly] = useState(false);
  const checkoutFilterOn = checkoutOnly && isCardView;
  const checkoutFilterNow = useNow(checkoutFilterOn);
  const displayedData = useMemo(() => {
    if (!checkoutFilterOn) return providerFilteredData;
    return providerFilteredData.filter(
      (row) => visitModel(row, checkoutFilterNow)?.checkoutDue,
    );
  }, [providerFilteredData, checkoutFilterOn, checkoutFilterNow]);
  // Status tab named in the provider filter's empty state ("No Active visits
  // for X"). Tabs only filter today's list and a patient search ignores them;
  // the All tab (last) reads as plain "visits".
  const providerEmptyTabLabel =
    tab &&
    !searchedUser &&
    dayKey(currentDate) === clinicTodayKey() &&
    Number(tab) !== DASHBOARD_TABS.length
      ? dashboardTabLabel(tab)
      : "";
  const providerEmptyVisits = providerEmptyTabLabel
    ? `${providerEmptyTabLabel} visits`
    : "visits";
  // A new day, status tab, patient search or view drops the filter, so a
  // searched patient is never hidden behind it.
  useEffect(() => {
    setCheckoutOnly(false);
  }, [currentDate, tab, searchedUser, isCardView]);
  const setSidenav = useStore((state) => state.setSidenav);
  const setmessagecount = useStore((state) => state.setMessagecount);
  const setPendingcount = useStore((state) => state.setPendingcount);
  const practiseid = getPracticeId();
  const [dashboardfilter, setDashboardfilter] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isfutureappointment, setIsFutureAppointment] = useState(false);
  const isloaderactive = useRef(true);
  const location = useLocation();
  // The patient search lives in the URL (/landing?q=<patientid>[&qall=1],
  // ./dashboardSearch), so closing a chart, coming back from the Orders page
  // or any remount returns to the same results. urlFetchedKeyRef: the search
  // whose rows are on screen (or being fetched), so a URL write made by a
  // search click does not fetch twice. Declared above every use.
  const urlSearch = readDashboardSearch(location.search);
  const urlSearchKey = dashboardSearchKey(urlSearch);
  const baseSearch = stripDashboardSearch(location.search);
  const urlFetchedKeyRef = useRef("");
  // The main fetch effect's previous inputs (it tells a search being cleared
  // -- whose clearer already refetched -- from a date/tab change).
  const effectDepsRef = useRef(null);
  // Window scroll to put back once the rows are on screen: coming back from
  // the Orders page, or a refresh replaced the list with the spinner.
  const pendingScrollRef = useRef(
    typeof location.state?.restoreScrollY === "number" ? location.state.restoreScrollY : null,
  );
  const dob = useRef("");
  const userConfig = useSelector((state) => state.userconfig);
  console.log(userConfig, "userConfig");
  const [scheduleStates, setScheduleStates] = useState({});
  const [chat, setChat] = useState(false);
  const data = useRef("");
  const navigate = useNavigate();
  //schedular -states
  const [screen, setScreen] = useState(0);
  const [isAptCard, setIsAptCard] = useState(false);
  const [appointmentCardopen, setIsappointmentCardOpen] = useState(false);
  // ✅ Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const gettab = queryParams.get("tab"); // "5"
  const subtab = queryParams.get("subtab"); // "1"
  const onmodal = queryParams.get("onmodal") === "true"; // true or false
  const date = queryParams.get("date"); // e.g., "2025-06-25"
  const [validpayments, setValidPayments] = useState([]);
  // ✅ Extract state object
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [rpmvalues, setRpmValues] = useState([]);
  const [toastSeverity, setToastSeverity] = useState("success");
  const handleToastClose = () => {
    setOpenToast(false);
  };

  /* ONE chart-open path (owner rule 2026-09-29: when the patient has an
     appointment today, a chart NEVER opens on a past appointment). Card
     clicks, the card's Vitals/Intake shortcuts, thread notifications and
     deep links all come here; searches, the sign-in sheet, Reports, the
     scheduler and Orders hand the patient to the dashboard, whose cards come
     here too. A card that is already the patient's non-cancelled visit today
     opens at once, exactly as before. Any other card (a past-date dashboard,
     the search fallback's newest past visit, a cancelled row) first looks for
     the patient's visit today and opens that instead. A past visit opens only
     when there is none, and the chart labels it "Past visit · 09-08 · ...".
     Declared above every use (the myData effect, openModal, the thread
     listener). */
  const chartOpenSeqRef = useRef(0);
  const chartResolvingRef = useRef(false);
  // Tab jumps (Vitals/Intake/Threads) fired while a chart open was resolving;
  // replayed once the chart is mounted.
  const pendingChartNavRef = useRef([]);
  const showChartCard = (card) => {
    setSidenav(false);
    setSelectedCard(card);
    setIsModalOpen(true);
  };
  // The patient's cards for today's clinic date (the resolver's search; also
  // the visit switch below).
  const searchTodayVisits = (pid) =>
    api.post(`/v1/${practiseid}/appointments/dashboard`, {
      appointmentdate: clinicTodayKey(),
      searchflag: String(pid),
      dashfilter: null,
      filterarr: [],
      start: 0,
      limit: 0,
      // Explicit patient search: today's visit counts at either location.
      alldepts: true,
    });
  // Resolves to the card the chart now shows (truthy), or false when nothing
  // was opened (no card, or a newer open superseded this one).
  const openChart = async (card) => {
    if (!card) return false;
    if (!needsChartResolution(card)) {
      // Supersede any open still resolving, so it cannot replace this chart.
      chartOpenSeqRef.current += 1;
      pendingChartNavRef.current = [];
      if (chartResolvingRef.current) {
        chartResolvingRef.current = false;
        setIsLoader(false);
      }
      showChartCard(card);
      return card;
    }
    const seq = ++chartOpenSeqRef.current;
    pendingChartNavRef.current = [];
    chartResolvingRef.current = true;
    setIsLoader(true);
    let result = null;
    try {
      result = await resolveChartVisit(card, {
        searchToday: searchTodayVisits,
        fetchFuture: (pid) =>
          api.get(`/v1/${practiseid}/appointments/${pid}/future`),
      });
    } catch (error) {
      console.log(error);
      const info = classifyVisit(card);
      result = { card, switched: false, verified: false, from: info, to: info };
    } finally {
      if (seq === chartOpenSeqRef.current) {
        chartResolvingRef.current = false;
        setIsLoader(false);
      }
    }
    // A newer open (another click) superseded this one.
    if (seq !== chartOpenSeqRef.current) return false;
    showChartCard(result.card);
    if (result.switched) {
      setToastSeverity("info");
      setToastMessage(switchedVisitMessage(result.from, result.to));
      setOpenToast(true);
    } else if (!result.verified && result.to?.kind !== "today") {
      setToastSeverity("warning");
      setToastMessage(
        `Could not check for a visit today. This chart is on: ${visitLabel(result.to)}.`,
      );
      setOpenToast(true);
    }
    const nav = pendingChartNavRef.current;
    pendingChartNavRef.current = [];
    if (nav.length) {
      setTimeout(() => {
        nav.forEach(([evt, payload]) => eventBus.emit(evt, payload));
      }, 100);
    }
    // The card shown (truthy, as before): the visit switch checks it is the
    // visit it asked for.
    return result.card || true;
  };
  useEffect(() => {
    const events = ["navigateToPriorVitals", "navigateToThreadsTab", "navigateToPriorVisit"];
    const handlers = events.map((evt) => {
      const h = (payload) => {
        if (chartResolvingRef.current) pendingChartNavRef.current.push([evt, payload]);
      };
      eventBus.on(evt, h);
      return [evt, h];
    });
    return () => handlers.forEach(([evt, h]) => eventBus.off(evt, h));
  }, []);

  useEffect(() => {
    document.title = `${practiceShortName()} - Dashboard`;
    if (!isModalOpen) {
      setSidenav(true);
    }
  }, [isModalOpen]);
  const { myData } = location.state || {};
  useEffect(() => {
    const hasQueryParams = gettab && subtab && date && onmodal !== null;
    const hasMyData =
      myData && typeof myData === "object" && Object.keys(myData).length > 0;

    if (hasQueryParams && hasMyData) {
      openChart(myData);

      //     setSearchedUser({
      //   patientid: myData.patientid,
      //  patientinfo: myData.patient_name
      // });
    }
  }, [tab, subtab, date, onmodal, myData]);
  let payload = {
    appointmentdate: currentDate,
    searchflag: state?.item?.patientid || null,
    dashfilter: tab,
    filterarr: [],
    start: 0,
    limit: 0,
  };

  // provider_master names for the provider filter: one GET /providers per
  // page load (cached in utils/providerDirectory). It resolves a reassigned
  // visit's linked_provider to that provider's id and gives each id its
  // label; until it answers, or if it fails, rows key by their booked
  // providerid.
  useEffect(() => {
    let alive = true;
    loadProviderDirectory(practiseid, (pid) => api.get(`/v1/${pid}/providers`))
      .then((directory) => {
        if (alive) setProviderDirectory(directory);
      })
      .catch((error) => console.log(error));
    return () => {
      alive = false;
    };
  }, [practiseid]);

  const fetchCount = async () => {
    try {
      const response = await api.post(
        `/v1/${practiseid}/appointments/dbscounts`,
        {
          type: "APT",
          userid: "all",
          searchdate: currentDate,
        },
      );
      if (response.status == 200) {
        if (response?.data?.length) {
          setDbCount(response.data[0]);
          setmessagecount(response?.data[0]?.messages || 0);
          setPendingcount(response?.data[0]?.pendingtasks || 0);
          // Default "Active" filter hides everything when almost nobody is
          // active (e.g. a small location's book, or early morning). If the
          // day's active count is under 2 and the user hasn't picked a tab,
          // fall back to the unfiltered view — once per load
          // (user request 2026-08-20).
          const activeCount = Number(response.data[0]?.active || 0);
          if (!autoRelaxedActiveRef.current && activeCount < 2 && tab === 1) {
            autoRelaxedActiveRef.current = true;
            dispatch(setTab(null)); // tab is an effect dep, so this refetches unfiltered
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  // const markAsComplete = async (ids, task) => {
  //   try {
  //     // Check if the task is an array or a single task object
  //     const taskToProcess = Array.isArray(task) ? task[0] : task; // Use the first task if it's an array

  //     if (!taskToProcess) {
  //       console.error("No task data available");
  //       return;
  //     }

  //     const payload = {
  //       ids: ids,
  //       status: taskToProcess.status,
  //       notes: taskToProcess.notes,
  //       orderdate: dayjs(taskToProcess.ts).format("YYYY-MM-DD"),
  //       login_user: userConfig?.username,
  //       ordertype: taskToProcess.ordertype,
  //       username: userConfig?.username,
  //     };

  //     // Make API call to mark as complete
  //     const response = await api.put(
  //       `/v1/${practiseid}/tasks/${taskToProcess.patientid}/mark_as_complete`,
  //       payload
  //     );

  //     if (response.status === 200) {

  //   for (let taskId of ids) {
  //     // Find the task by ID from the tasks array
  //     const taskToAthena = task.find(tasks => tasks.id === taskId);
  //       if (
  //         taskToAthena.ordername === "COVID TEST" ||
  //         taskToAthena.ordername === "STREP TEST" ||
  //         taskToAthena.ordername === "FLU TEST"
  //       ) {
  //           const appurl = window.location.origin;
  //           const fileUrl = `${appurl}/imaging/strep-covid-flu-Preview/${ids}`;

  //           try {
  //             const observationDate = dayjs().format("MM/DD/YYYY");
  //             const observationTime = dayjs().format("HH:mm");
  //             const departmentid = 1;

  //             // Create a hidden iframe to load the preview page
  //             const iframe = document.createElement("iframe");
  //             iframe.style.position = "fixed";
  //             iframe.style.top = "-10000px";
  //             iframe.style.left = "-10000px";
  //             iframe.style.width = "1200px";
  //             iframe.style.height = "900px";
  //             iframe.style.border = "none";
  //             document.body.appendChild(iframe);

  //             // Wait for iframe to load
  //             await new Promise((resolve, reject) => {
  //               iframe.onload = resolve;
  //               iframe.onerror = () => reject(new Error("Failed to load preview page"));
  //               iframe.src = fileUrl;
  //             });

  //             // Wait for the report to be fully rendered (has id="report-ready")
  //             const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
  //             let reportElement = null;
  //             for (let i = 0; i < 20; i++) {
  //               reportElement = iframeDoc.getElementById("report-ready");
  //               if (reportElement) break;
  //               await new Promise((res) => setTimeout(res, 500));
  //             }

  //             // Fallback to body if report element not found
  //             const captureElement = reportElement || iframeDoc.body;

  //             // Capture only the report element as a canvas
  //             const canvas = await html2canvas(captureElement, {
  //               useCORS: true,
  //               allowTaint: true,
  //               scale: 2,
  //               backgroundColor: "#ffffff",
  //             });

  //             // Remove the iframe after capture
  //             document.body.removeChild(iframe);

  //             // Convert canvas to JPG blob
  //             const fileBlob = await new Promise((resolve) =>
  //               canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95)
  //             );

  //             const formData = new FormData();
  //             formData.append("file", fileBlob, "file.jpg");
  //             formData.append("attachmenttype", "JPG");
  //             formData.append("observationdate", observationDate);
  //             formData.append("observationtime", observationTime);
  //             formData.append("autoclose", true);

  //             const uploadResponse = await api.post(
  //               `/v1/${practiseid}/${departmentid}/patient/${taskToProcess.patientid}/add-strepcovidflu-athena`,
  //               formData,
  //               {
  //                 headers: {
  //                   "Content-Type": "multipart/form-data",
  //                 },
  //               }
  //             );

  //             setToastMessage(
  //               `File uploaded successfully: ${JSON.stringify(uploadResponse.data)}`
  //             );
  //           } catch (error) {
  //             setToastMessage(`Request failed: ${error.message}`);
  //           } finally {
  //             setLoading(false);
  //           }
  //       }
  //   }

  //       // Refetch the data after completing the task
  //       setToastSeverity("success");
  //       setToastMessage(response.message);
  //       setOpenToast(true);

  //       // Refresh dashboard data to show updated task status
  //    await fetchDashboardData({
  //      searchflag:fetchedData?.length>1?"": (taskToProcess?.patientid || ""),
  //         filter: appliedFilter || "",
  //         labels: selectedFilterName || {},
  //         appointmentdate: currentDate,
  //       });

  //       // Also refresh the counts
  //       await fetchCount();

  //       console.log("Task marked as complete successfully");
  //     } else {
  //       setToastSeverity("info");
  //       setToastMessage(response.message);
  //       setOpenToast(true);
  //       console.error("Error marking task as complete");
  //     }
  //   } catch (error) {
  //     setToastSeverity("error");
  //     setToastMessage("Something Went Wrong");
  //     setOpenToast(true);
  //     console.error("Error marking task as complete:", error);
  //   }
  // };

  // Returns the gateway's answer (null when the call failed), so the card's
  // "Mark done & check out" checks out only after a completion went through.
  // The other callers ignore it.
  const markAsComplete = async (ids, tasks) => {
    try {
      // Check if the task is an array or a single task object
      const taskToProcess = Array.isArray(tasks) ? tasks[0] : tasks; // Use the first task if it's an array

      if (!taskToProcess) {
        console.error("No task data available");
        return;
      }

      const payload = {
        ids: ids,
        status: taskToProcess.status,
        notes: taskToProcess.notes,
        orderdate: dayjs(taskToProcess.ts).format("YYYY-MM-DD"),
        login_user: userConfig?.username,
        ordertype: taskToProcess.ordertype,
        username: userConfig?.username,
      };

      // Make API call to mark as complete
      const response = await api.put(
        `/v1/${practiseid}/tasks/${taskToProcess.patientid}/mark_as_complete`,
        payload,
      );

      if (response.status === 200) {
        // In-house COVID/STREP/FLU tests (src/tasks/inhouseTestUpload.js): filed once
        // per test - the gateway answers a repeat with the copy already in Athena - and
        // a failed upload is resent by selecting the completed test and clicking again.
        // Every outcome goes into ONE toast; failures are red, never hidden (review 10-06).
        const uploads = await sendInHouseResults({
          practiseid,
          tasks: inHouseTasksToUpload(ids, response, Array.isArray(tasks) ? tasks : [tasks]),
          fallbackPatientid: taskToProcess.patientid,
        });
        setLoading(false);

        // Refetch the data after completing the task
        const summary = inHouseSummaryToast(response.message, uploads);
        setToastSeverity(summary.severity);
        setToastMessage(summary.message);
        setOpenToast(true);

        // Refresh dashboard data to show updated task status
        await fetchDashboardData({
          searchflag:
            fetchedData?.length > 1 ? "" : taskToProcess?.patientid || "",
          filter: appliedFilter || "",
          labels: selectedFilterName || {},
          appointmentdate: currentDate,
        });

        // Refresh the counts
        await fetchCount();

        console.log("Task marked as complete successfully");
        return response;
      } else {
        setToastSeverity("info");
        setToastMessage(response.message);
        setOpenToast(true);
        console.error("Error marking task as complete");
        return response;
      }
    } catch (error) {
      setToastSeverity("error");
      setToastMessage("Something Went Wrong");
      setOpenToast(true);
      console.error("Error marking task as complete:", error);
      return null;
    }
  };

  const markAscancel = async (ids, task) => {
    try {
      // Check if the task is an array or a single task object
      const taskToProcess = Array.isArray(task) ? task[0] : task; // Use the first task if it's an array

      if (!taskToProcess) {
        console.error("No task data available");
        return;
      }

      // Construct the payload for the cancel action
      const payload = {
        ids: ids, // Pass the task ids (task.id or array of ids)
        notes: taskToProcess.notes, // Use notes from the task
        st_date: dayjs(taskToProcess.ts).format("YYYY-MM-DD"), // Format the timestamp
        login_user: userConfig?.username, // Logged in user info
      };

      // Make API call to mark as decline (cancel)
      const response = await api.put(
        `/v1/${practiseid}/tasks/mark_as_decline`,
        payload,
      );

      if (response.status === 200) {
        setToastSeverity("success");
        setToastMessage(response.message);
        setOpenToast(true);

        // Refresh dashboard data to show updated task status
        await fetchDashboardData({
          searchflag:
            fetchedData?.length > 1 ? "" : taskToProcess?.patientid || "",
          filter: appliedFilter || "",
          labels: selectedFilterName || {},
          appointmentdate: currentDate,
        });

        // Also refresh the counts
        await fetchCount();

        console.log("Task marked as cancelled successfully");
      } else {
        setToastSeverity("info");
        setToastMessage(response.message);
        setOpenToast(true);
        console.error("Error marking task as cancelled");
      }
    } catch (error) {
      setToastSeverity("error");
      setToastMessage("Something Went Wrong");
      setOpenToast(true);
      console.error("Error marking task as cancelled:", error);
    }
  };

  const handleconfirmScheduleClick = async (order) => {
    try {
      // Create the payload to send the status update

      const newStatusFlag = selectedOrder.schedule === "t" ? "f" : "t"; // Toggle status for this specific order

      console.log(newStatusFlag);
      setScheduleStates((prevState) => ({
        ...prevState,
        [order.id]: newStatusFlag, // Update only this specific order's status
      }));

      // Create the payload to send the status update
      const payload = {
        orderid: order.id,
        statusflag: newStatusFlag,
        login_user: userConfig.username,
      };
      // Call API to update status flag
      const response = await api.put(
        `/v1/${practiseid}//tasks/update_status_schedule`,
        payload,
      );

      if (response.status === 200) {
        // If the update was successful, refresh data if necessary
        //  fetchdata({ dt: selectedDate.format('YYYY-MM-DD'), st: selectedStatus,ct:selectedCategory, searchQuery,isfilter:true });
        setScheduleStates({});
      } else {
        console.error("Error updating status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };
  const insertdata = (X) => {
    data.current = X;

    getdob(X?.patientid);

    setImagingModal(true);
  };

  // Request body for POST /appointments/dashboard from fetch params. Shared by
  // fetchDashboardData and the silent background refresh, so a replay asks
  // for exactly what the last fetch did.
  const buildDashboardPayload = ({
    searchflag,
    filter = "",
    appointmentdate,
    tabFilter,
    alldepts,
  }) => {
    // Create a new payload object to avoid mutating the original
    const requestPayload = {
      appointmentdate: appointmentdate || currentDate,
      searchflag: state?.item?.patientid || null,
      dashfilter: tabFilter || tab, // Use tabFilter if provided, otherwise fall back to Redux tab
      filterarr: [],
      start: 0,
      limit: 0,
      // Carried from the search bar's "All depts" checkbox — without this a
      // cross-department patient found via the checkbox vanished the moment
      // their suggestion was clicked (QA 2026-08-20, 13902).
      ...(alldepts && { alldepts: true }),
    };

    // Status tabs (All included) filter today's list only. "Today" is the
    // clinic's calendar day: the old check compared UTC dates, so from 7 pm
    // CDT every tab silently stopped filtering.
    if (requestPayload.dashfilter) {
      const appointmentDay = String(requestPayload.appointmentdate || "").slice(0, 10);
      if (appointmentDay !== clinicTodayKey()) {
        requestPayload.dashfilter = null;
      }
    }
    if (searchflag) {
      requestPayload.searchflag = searchflag;
      requestPayload.dashfilter = null;
    }
    if (filter) {
      requestPayload.filterarr = [filter];
    }
    return requestPayload;
  };

  /* The provider filter's options come from the WHOLE day (dayRows), not the
     loaded status tab. A fetch that already covers the day (no tab - e.g. a
     past/future date or the unfiltered view - or the All tab) is reused as
     is. Otherwise one extra tab-less dashboard call is made: once per date
     (switching tabs on the same day re-uses it), and after that at most once
     per DAY_ROWS_REFRESH_MS, from the silent background refresh. Options
     also merge the loaded rows, so a reassignment shows at once anyway. */
  // The day on screen (kept current every render: an answer for any other
  // day - e.g. a slow fetch from before a date change - is dropped and never
  // moves anything), the day the stored rows are for and the day being
  // fetched now.
  const dayWantedRef = useRef("");
  dayWantedRef.current = dayKey(currentDate);
  const dayRowsDateRef = useRef("");
  const dayRowsLocRef = useRef(""); // the location the stored rows are for
  const dayFetchInFlightRef = useRef("");
  const dayRowsAtRef = useRef(0); // when dayRows were last stored
  // Last extra call made (day + time), so a failing one is not retried on
  // every background tick either.
  const dayAttemptRef = useRef({ day: "", at: 0 });
  // `location`: the header location the rows were fetched for; rows from
  // before a switch are dropped.
  const storeDayRows = (date, rows, location = workingLocRef.current) => {
    if (dayKey(date) !== dayWantedRef.current) return;
    if (location !== workingLocRef.current) return;
    dayRowsDateRef.current = dayKey(date);
    dayRowsLocRef.current = location;
    dayRowsAtRef.current = Date.now();
    setDayRows({ date: dayKey(date), location, rows: Array.isArray(rows) ? rows : [] });
  };
  const fetchDayRows = async (date) => {
    const day = dayKey(date);
    const location = workingLocRef.current;
    const flight = `${day}|${location}`;
    if (!day || day !== dayWantedRef.current || dayFetchInFlightRef.current === flight) return;
    dayFetchInFlightRef.current = flight;
    dayAttemptRef.current = { day: flight, at: Date.now() };
    try {
      const response = await api.post(
        `/v1/${practiseid}/appointments/dashboard`,
        {
          appointmentdate: day,
          searchflag: null,
          dashfilter: null,
          filterarr: [],
          start: 0,
          limit: 0,
        },
      );
      if (
        mountedRef.current &&
        dayWantedRef.current === day &&
        response?.status === 200 &&
        Array.isArray(response.data)
      ) {
        storeDayRows(day, response.data, location);
      }
    } catch (error) {
      console.log(error); // options fall back to the loaded rows
    } finally {
      if (dayFetchInFlightRef.current === flight) dayFetchInFlightRef.current = "";
    }
  };
  // Called after a dashboard fetch with its request body and rows.
  // `location`: the header location the request was made for.
  const syncDayRows = (requestPayload, rows, refresh = false, location = workingLocRef.current) => {
    if (isWholeDayPayload(requestPayload)) {
      storeDayRows(requestPayload.appointmentdate, rows, location);
      return undefined;
    }
    const day = dayKey(requestPayload.appointmentdate);
    const now = Date.now();
    const attempt = dayAttemptRef.current;
    const attemptDue =
      attempt.day !== `${day}|${location}` || now - attempt.at >= DAY_ROWS_REFRESH_MS;
    const needed =
      dayRowsDateRef.current !== day || dayRowsLocRef.current !== location
        ? !refresh || attemptDue // new date: fetch (a tick retries a failure every 10 min)
        : refresh && attemptDue && now - dayRowsAtRef.current >= DAY_ROWS_REFRESH_MS;
    if (needed) {
      return fetchDayRows(requestPayload.appointmentdate);
    }
    return undefined;
  };

  const fetchDashboardData = async ({
    searchflag,
    filter = "",
    labels,
    appointmentdate,
    tabFilter,
    alldepts,
  }) => {
    console.log("filter", filter);
    lastFetchParamsRef.current = { searchflag, filter, labels, appointmentdate, tabFilter, alldepts };

    setSelectedFilterName(labels);
    if (filter && typeof filter === "object") setAppliedFilter(filter);
    setLoading(true);
    const seq = ++dashFetchSeqRef.current;

    const requestPayload = buildDashboardPayload({
      searchflag,
      filter,
      appointmentdate,
      tabFilter,
      alldepts,
    });
    dashFetchesInFlightRef.current += 1;
    // The location this request is answered for (the server reads it per
    // request); a switch while it is in flight must not file its rows under
    // the new location.
    const requestLocation = workingLocRef.current;
    try {
      const response = await api.post(
        `/v1/${practiseid}/appointments/dashboard`,
        requestPayload,
      );
      // A newer request was made meanwhile: drop this answer.
      if (seq !== dashFetchSeqRef.current) return;
      if (response.status === 200) {
        const newData = response.data || [];
        // console.log('State',state)
        // if (state?.item?.patientid) {
        //   newData = newData.filter((item) => item.patientid === state?.item?.patientid);
        //   console.log("Filtered Data", newData);

        // }
        setFetchedData(newData);
        // Not awaited: the list is on screen; the options catch up.
        syncDayRows(requestPayload, newData, false, requestLocation);
      } else {
        setFetchedData([]);
      }
    } catch (error) {
      console.log(error);
      if (seq === dashFetchSeqRef.current) setFetchedData([]);
    } finally {
      dashFetchesInFlightRef.current = Math.max(0, dashFetchesInFlightRef.current - 1);
      lastDashFetchAtRef.current = Date.now();
      // The newest request owns the spinner.
      if (seq === dashFetchSeqRef.current) setLoading(false);
    }
  };

  // Helper to refresh dashboard with current filters/date
  const refreshDashboard = async (patientId) => {
    await fetchDashboardData({
      searchflag: patientId || "",
      filter: appliedFilter || "",
      labels: selectedFilterName || {},
      appointmentdate: currentDate,
    });
    await fetchCount();
  };

  /* Silent background refresh. The visit chips, the "Check out?" reminder and
     the toolbar count run a live clock against this data, and nothing pushes
     changes made on another screen (a provider stopping a recording on their
     own device). So while TODAY's dashboard is on screen it is re-fetched
     quietly: the exact params of the last fetch (a search or filter view
     stays on the same patients, as with refreshDashboard), no spinner, no
     count/tab side effects, never overlapping another dashboard fetch, and
     only when nothing is open or being typed in, so modals and expanded
     cards keep their state. If anything changed the list meanwhile (a normal
     fetch, a card's own update) the result is dropped; the next run catches
     up. Always called through silentRefreshRef, so it reads this render's
     state. */
  const silentRefreshDashboard = async () => {
    const params = lastFetchParamsRef.current;
    if (!params || bgRefreshInFlightRef.current || dashFetchesInFlightRef.current > 0) return;
    if (loading || isloader || document.visibilityState !== "visible") return;
    const today = clinicTodayKey();
    if (currentDate !== today || (params.appointmentdate || currentDate) !== today) return;
    if (
      isModalOpen ||
      isPcard ||
      chat ||
      imagingmodal ||
      filtermodal ||
      dashboardfilter ||
      isStatusNotesPopUp ||
      isAptCard
    )
      return;
    if (document.querySelector(BG_REFRESH_OVERLAY_SELECTOR)) return;
    const focused = document.activeElement;
    if (
      focused &&
      (focused.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(focused.tagName))
    )
      return;
    const before = fetchedData;
    bgRefreshInFlightRef.current = true;
    try {
      const requestPayload = buildDashboardPayload(params);
      const requestLocation = workingLocRef.current;
      const response = await api.post(
        `/v1/${practiseid}/appointments/dashboard`,
        requestPayload,
      );
      if (!mountedRef.current || lastFetchParamsRef.current !== params) return;
      if (response?.status === 200 && Array.isArray(response.data)) {
        const newData = response.data;
        setFetchedData((prev) => (prev === before ? newData : prev));
        // Keep the tab counts in step too (a patient checked out on another
        // screen otherwise left Checked Out stale until a reload).
        fetchCount();
        // The day's provider set: free when this view covers the day,
        // otherwise one tab-less call at most every DAY_ROWS_REFRESH_MS.
        await syncDayRows(requestPayload, newData, true, requestLocation);
      }
    } catch (error) {
      console.log(error); // keep what is on screen
    } finally {
      bgRefreshInFlightRef.current = false;
      lastDashFetchAtRef.current = Date.now();
    }
  };
  silentRefreshRef.current = silentRefreshDashboard;

  useEffect(() => {
    mountedRef.current = true;
    let wakeTimer = null;
    const run = () => {
      if (Date.now() - lastDashFetchAtRef.current < BG_REFRESH_MIN_GAP_MS) return;
      if (silentRefreshRef.current) silentRefreshRef.current();
    };
    const interval = setInterval(run, BG_REFRESH_INTERVAL_MS);
    // Focus and visibilitychange usually fire together: one run.
    const onWake = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(wakeTimer);
      wakeTimer = setTimeout(run, BG_REFRESH_DEBOUNCE_MS);
    };
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onWake);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      clearTimeout(wakeTimer);
      window.removeEventListener("focus", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, []);

  // The router state to keep on a URL rewrite, minus one-shot keys a re-read
  // would act on again (a deep link's myData would reopen its chart).
  const keptRouterState = () => {
    if (!location.state || typeof location.state !== "object") return null;
    const { myData: _m, fromPatientId: _f, restoreScrollY: _r, ...rest } = location.state;
    return Object.keys(rest).length ? rest : null;
  };
  // Mirror the searched patient into the URL (replace: no extra history
  // entry).
  const syncSearchUrl = (user) => {
    searchSeqRef.current += 1;
    const value =
      user && user.patientid
        ? { patientid: String(user.patientid), alldepts: Boolean(user.alldepts) }
        : null;
    if (value) {
      // The caller fetches this search itself.
      urlFetchedKeyRef.current = dashboardSearchKey(value);
      saveSearchChip({ ...user, dob: user.dob || dob.current });
    }
    const next = withDashboardSearch(location.search, value);
    if (next === location.search) return;
    navigate(`${location.pathname}${next}`, { replace: true, state: keptRouterState() });
  };
  // The toolbar's search: picking a patient / clearing the chip.
  const selectSearchedUser = (user) => {
    setSearchedUser(user);
    syncSearchUrl(user);
  };
  // Function to clear search user
  const clearSearchUser = () => {
    setSearchedUser(null);
    syncSearchUrl(null);
  };

  // Rebuild a search from the URL (a remount: back from the Orders page, a
  // reload, a location-switch remount): the chip from this tab's cache, else
  // looked up by id, and the same dashboard query as the original click.
  const restoreUrlSearch = async (search) => {
    const key = dashboardSearchKey(search);
    const searchSeq = ++searchSeqRef.current;
    const current = () => searchSeq === searchSeqRef.current && urlFetchedKeyRef.current === key;
    const cached = loadSearchChip(search.patientid);
    if (cached) {
      dob.current = cached.dob || "";
      setSearchedUser({
        patientid: cached.patientid,
        patientinfo: cached.patientinfo,
        lastname: cached.lastname,
        alldepts: search.alldepts,
      });
    }
    const rows = fetchDashboardData({
      searchflag: search.patientid,
      ...(search.alldepts && { alldepts: true }),
    });
    if (!cached) {
      try {
        const response = await api.post(`/v1/${practiseid}/autocomplete/dssearch`, {
          searchtxt: search.patientid,
          alldepts: search.alldepts,
        });
        // Superseded (cleared or another search) while this was in flight.
        if (!current()) return;
        const list = response?.status === 200 && Array.isArray(response.data) ? response.data : [];
        // The id's own row only: a text search can match other patients too.
        const patient = list.find((p) => String(p?.patientid) === String(search.patientid));
        if (patient?.dob) dob.current = patient.dob;
        const chip = {
          patientid: search.patientid,
          patientinfo: patient?.patientinfo || `Patient ${search.patientid}`,
          lastname: patient?.lastname || "",
          alldepts: search.alldepts,
        };
        setSearchedUser(chip);
        if (patient) saveSearchChip({ ...chip, dob: patient.dob });
      } catch (error) {
        console.log(error);
        if (current()) {
          setSearchedUser({
            patientid: search.patientid,
            patientinfo: `Patient ${search.patientid}`,
            alldepts: search.alldepts,
          });
        }
      }
    }
    await rows;
  };

  const hasHandledFromPatient = useRef(false);
  const appliedFilterRef = useRef(appliedFilter);
  const selectedFilterNameRef = useRef(selectedFilterName);

  // Update refs when filters change
  useEffect(() => {
    appliedFilterRef.current = appliedFilter;
    selectedFilterNameRef.current = selectedFilterName;
  }, [appliedFilter, selectedFilterName]);

  useEffect(() => {
    const prev = effectDepsRef.current;
    const cur = { currentDate, tab, pathname: location.pathname, baseSearch, urlSearchKey };
    effectDepsRef.current = cur;
    // Only the search went away (chip removed, Refresh, Clear all): whoever
    // cleared it has already loaded the view that replaces it.
    const onlySearchCleared =
      !!prev &&
      !!prev.urlSearchKey &&
      !cur.urlSearchKey &&
      prev.currentDate === cur.currentDate &&
      prev.tab === cur.tab &&
      prev.pathname === cur.pathname &&
      prev.baseSearch === cur.baseSearch;
    const run = async () => {
      const fromPatientId = location.state?.fromPatientId;
      try {
        // A patient handed over by another page (Schedule, Inbox, Fax, the
        // Orders page's old return): once, as a URL search, so it is kept
        // like one picked in the search box. The effect re-runs on the URL.
        if (fromPatientId && !hasHandledFromPatient.current) {
          hasHandledFromPatient.current = true;
          navigate(
            `${location.pathname}${withDashboardSearch(location.search, {
              patientid: String(fromPatientId),
              alldepts: false,
            })}`,
            { replace: true, state: null },
          );
          return;
        }

        // A search in the URL: its results, never the plain dashboard. A
        // date or tab change made while it is on screen (the low-census
        // auto-relax of the Active tab) does not drop it either; the
        // toolbar's own date/tab actions clear the search themselves.
        if (urlSearch) {
          if (urlFetchedKeyRef.current === urlSearchKey) return;
          urlFetchedKeyRef.current = urlSearchKey;
          await restoreUrlSearch(urlSearch);
          fetchCount();
          getvalidpayments();
          return;
        }
        // The URL lost the search but the search was not cleared (a plain
        // navigate("/landing") from inside the chart, e.g. a note's Save, or
        // the header's Home on this page): put it back. Those navigations
        // never reset the results before either.
        if (onlySearchCleared && searchedUser?.patientid) {
          const keep = {
            patientid: String(searchedUser.patientid),
            alldepts: Boolean(searchedUser.alldepts),
          };
          urlFetchedKeyRef.current = dashboardSearchKey(keep);
          navigate(`${location.pathname}${withDashboardSearch(location.search, keep)}`, {
            replace: true,
            state: keptRouterState(),
          });
          return;
        }
        urlFetchedKeyRef.current = "";
        if (onlySearchCleared) return;

        if (currentDate) {
          payload.appointmentdate = currentDate;
          // Preserve existing filters when date changes
          fetchDashboardData({
            searchflag: "",
            filter: appliedFilterRef.current || "",
            labels: selectedFilterNameRef.current || {},
          });
          console.log("calling from date");
          fetchCount();
          getvalidpayments();
        } else {
          fetchDashboardData({ searchflag: "" });
          getvalidpayments();
          console.log("calling empty");
        }
      } catch (error) {
        console.log(error);
      } finally {
        //setLoading(false)
      }
    };

    run();
  }, [currentDate, tab, location.pathname, baseSearch, urlSearchKey, navigate]);

  // Put the scroll position back once the rows are on screen (see
  // pendingScrollRef).
  // Cleared once that load finishes, rows or not, so a later load never
  // jumps to a stale position.
  useEffect(() => {
    if (loading) {
      sawLoadingRef.current = true;
      return;
    }
    if (!sawLoadingRef.current) return;
    sawLoadingRef.current = false;
    if (pendingScrollRef.current == null) return;
    const y = pendingScrollRef.current;
    pendingScrollRef.current = null;
    if (!fetchedData?.length) return;
    try {
      window.scrollTo(0, y);
    } catch (e) {
      /* jsdom / old browsers: the list is still right, only not scrolled */
    }
  }, [loading, fetchedData]);

  // Card "Chart" / Vitals / Intake: through the one chart-open resolver.
  const openModal = (x) => openChart(fetchedData?.find((y) => y.id === x));

  // Listen for thread notification click to open patient chart at Threads tab
  useEffect(() => {
    const handleOpenPatientThread = (payload) => {
      const { patientid } = payload || {};
      if (!patientid) return;
      const card = fetchedData?.find((y) => y.patientid === patientid);
      if (card) {
        // Through the chart-open resolver (today's visit when there is one).
        openChart(card).then((opened) => {
          if (!opened) return;
          setTimeout(() => {
            eventBus.emit("navigateToThreadsTab", { tab: 10 });
          }, 100);
        });
      }
    };

    eventBus.on("openPatientThread", handleOpenPatientThread);
    return () => {
      eventBus.off("openPatientThread", handleOpenPatientThread);
    };
  }, [fetchedData]);

  // Recording start: "record on my own visit" (another user's signed note on
  // the chart's visit, 10-06). Re-points the open chart to that visit through
  // openChart, exactly as clicking its card does; answers with the card shown.
  useEffect(() => {
    const handleOpenChartVisit = (payload) => {
      const respond = typeof payload?.respond === "function" ? payload.respond : () => {};
      openChartVisitCard(payload, { rows: fetchedData, searchToday: searchTodayVisits, openChart })
        .then(respond, () => respond(null));
    };
    eventBus.on(OPEN_CHART_VISIT, handleOpenChartVisit);
    return () => {
      eventBus.off(OPEN_CHART_VISIT, handleOpenChartVisit);
    };
  });

  // Card rows carry an order/notes summary computed server-side, so anything
  // that changes them (CareIQ order create, recording finish) emits
  // refreshDashboard; without this the change only shows after a full reload.
  // Replays the exact params of the last fetch so a searched or filtered view
  // stays on the same patients.
  useEffect(() => {
    const handleRefresh = () => {
      if (lastFetchParamsRef.current) {
        // The spinner replaces the list while it loads: keep the place.
        pendingScrollRef.current = window.scrollY || 0;
        fetchDashboardData(lastFetchParamsRef.current);
      }
    };
    eventBus.on("refreshDashboard", handleRefresh);
    return () => {
      eventBus.off("refreshDashboard", handleRefresh);
    };
  });
  const getvalidpayments = async () => {
    try {
      const reponse = await api.get(`/v1/${practiseid}/lookup_master/valid`);

      const rpmdetails = await api.get(
        `/v1/${practiseid}/rpm/bpp-master/latest`,
      );
      if (rpmdetails.status == 200) {
        setRpmValues(rpmdetails?.data || []);
      } else {
        setRpmValues([]);
      }
      if (reponse.status == 200) {
        setValidPayments(reponse.data || []);
      } else {
        setValidPayments([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };
  const fetchpcarddata = (x, ref) => {
    // The chart's card may be today's visit resolved from another day's card
    // (not in the loaded list): keep it rather than clearing the card. Match
    // the id AND the card's appointment (or patient): appointment_master and
    // appointment_master_history ids overlap, so an id alone can pick another
    // patient's card.
    const row = findPcardRow(fetchedData, x, ref);
    if (row) setSelectedCard(row);
    setIsPcard(true);
  };
  const closeModal = () => {
    // setSidenav(true);
    setIsModalOpen(false);
  };
  const getdob = async (query) => {
    try {
      setLoading(true);
      const response = await api.post(
        `/v1/${practiseid}/autocomplete/dssearch`,
        {
          searchtxt: query,
        },
      );
      if (response.status === 200) {
        const fetchedDob = response.data?.[0]?.dob;
        if (fetchedDob) {
          // ensures it's not null, undefined, or empty
          dob.current = fetchedDob;
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleschedular = async (appointment) => {
    try {
      console.log(appointment, "appointment");
      setLoading(true);
      const payload = {
        startdate: currentDate,
      };

      const response = await api.post(
        `/v1/${practiseid}/scheduler/schedule_list`,
        payload,
      );
      if (response.status == 200 && response?.data?.length) {
        console.log("go to if", appointment.patientid);
        const filteredAppointments = response?.data.filter((app) => {
          return String(app.patientid) === String(appointment?.patientid);
        });

        console.log(filteredAppointments, "filter");
        if (filteredAppointments?.length) {
          setIsFutureAppointment(false);
          console.log("appointment1 ", appointment);
          setSelectedAppointment({
            ...filteredAppointments[0],
            signintime: appointment?.signintime ?? null,
          });

          setScreen(1);
          setIsAptCard(true);
        } else {
          console.log("camehere");
          const appointmentsdata = await api.get(
            `/v1/${practiseid}/appointments/${appointment?.patientid}/future`,
          );
          if (appointmentsdata.status == 200) {
            setIsFutureAppointment(true);
            console.log("appointment2 ", appointment);
            setSelectedAppointment({
              ...(appointmentsdata?.data?.[0] || {}),
              signintime: appointment?.signintime ?? null,
            });

            setScreen(1);
            setIsAptCard(true);
          }
        }
      } else {
        const appointmentsdata = await api.get(
          `/v1/${practiseid}/appointments/${appointment?.patientid}/future`,
        );
        if (appointmentsdata.status == 200) {
          setIsFutureAppointment(true);
          console.log("appointment3 ", appointment);
          setSelectedAppointment({
            ...(appointmentsdata?.data?.[0] || {}),
            signintime: appointment?.signintime ?? null,
          });
          setScreen(1);
          setIsAptCard(true);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   if (fromPatientId) {
  //     fetchDashboardData(fromPatientId);

  //     // Remove `fromPatientId` from location.state after fetching
  //     navigate(location.pathname + location.search, {
  //       replace: true,
  //       state: { ...myData }
  //     });
  //   }
  // }, [fromPatientId, location.pathname, location.search, navigate]);
  const refereshpcard = async () => {
    try {
      setLoading(true);
      const requestPayload = {
        appointmentdate: currentDate,
        searchflag: selectedCard.patientid || null,
        dashfilter: tab, // Use tabFilter if provided, otherwise fall back to Redux tab
        filterarr: [],
        start: 0,
        limit: 0,
      };

      const response = await api.post(
        `/v1/${practiseid}/appointments/dashboard`,
        requestPayload,
      );
      if (response.status == 200) {
        const updatedPatient = response.data[0];

        setSelectedCard(updatedPatient);

        setFetchedData((prev) =>
          prev.map(
            (item) =>
              item.patientid === updatedPatient.patientid
                ? updatedPatient // replace
                : item, // keep old
          ),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const updateeyeicon = async (order) => {
    try {
      const response = await api.put(
        `/v1/${practiseid}/orders/${order?.patientid}/update-hide-status`,
        {
          id: order.id,
          orderdate: dayjs(order.ts).format("YYYY-MM-DD"),
          is_hide: "Y",
        },
      );
      if (response.status === 200) {
        // 🧠 update only that order in fetchedData
        setFetchedData((prev) =>
          prev.map((patient) => {
            if (patient.patientid !== order.patientid) return patient;

            return {
              ...patient,
              orders: patient.orders.map((o) =>
                o.id === order.id
                  ? { ...o, is_hide: "Y" } // update locally
                  : o,
              ),
            };
          }),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUpdateFrontDesk = async ({ frontDesk, data }) => {
    try {
      setIsLoader(true);
      const response = await api.put(
        `/v1/${practiseid}/appointments/patient/${data.patientid}/front_desk`,
        {
          appointmentid: data.appointmentid,
          is_front_desk: frontDesk,
        },
      );
      if (response.status === 200) {
        setFetchedData((prevData) =>
          prevData.map((item) =>
            item.id === data?.id ? { ...item, is_front_desk: frontDesk } : item,
          ),
        );
      }
    } catch (error) {
      console.error("Error updating front desk:", error);
    } finally {
      setIsLoader(false);
    }
  };
  return (
    <div className="h-full">
      <SideNavLayout>
        {/* pb-3 extends the opaque band below the toolbar, so cards scrolling
            under it keep a gap instead of butting against its edge. */}
        <div className="sticky bg-[#FAFAF9] top-16 z-20 pt-4 pb-3">
          <NavigationMenu
            selectedFilterName={selectedFilterName}
            setSelectedFilterName={setSelectedFilterName}
            appliedFilter={appliedFilter}
            setAppliedFilter={setAppliedFilter}
            setFiltermodal={setFiltermodal}
            dob={dob}
            dbscount={dbscount}
            isCardView={isCardView}
            setIsCardView={setIsCardView}
            setStartDate={setStartDate}
            startdate={startdate}
            fetchDashboardData={fetchDashboardData}
            setDashboardfilter={setDashboardfilter}
            searchedUser={searchedUser}
            setSearchedUser={selectSearchedUser}
            clearSearchUser={clearSearchUser}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            /* Rendered inside the status-pill row, right of the pills. */
            providerFilterSlot={
              <>
                {/* List view only: tiles show no "Check out?" reminder. */}
                {isCardView ? (
                  <CheckoutDueChip
                    rows={providerFilteredData}
                    active={checkoutFilterOn}
                    onToggle={() => setCheckoutOnly((v) => !v)}
                  />
                ) : null}
                {/* Shown with a single provider too: it says whose queue
                   this is and keeps the selection clearable. */}
                {providerOptions.length > 0 ? (
                  <ProviderFilter
                    options={providerOptions}
                    selected={providerFilter}
                    setSelected={setProviderFilter}
                  />
                ) : null}
              </>
            }
          />
        </div>
        {!fetchedData ||
        (fetchedData.length === 0 && !loading && isloaderactive.current) ? (
          <div className="w-full py-10 flex items-center justify-center font-normal text-xl text-[#000000]">
            No appointments found
          </div>
        ) : (
          <div
            /* pl-1/pr-4 evens the gutters: SideNavLayout already pads 28px on
               the left to clear the rail toggle, against p-4 on the right. */
            className={`mt-6 px-4 ${
              !isCardView
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 items-stretch"
                : "flex flex-col gap-6 items-stretch"
            }`}
          >
            {loading ? (
              <div className="flex justify-center items-center w-full mt-8">
                <CircularProgress />
              </div>
            ) : checkoutFilterOn && displayedData.length === 0 ? (
              <div className="w-full py-10 flex items-center justify-center font-normal text-xl text-[#000000]">
                No patients waiting to check out
              </div>
            ) : providerFilter.length > 0 && displayedData.length === 0 ? (
              <div className="w-full py-10 flex items-center justify-center font-normal text-xl text-[#000000]">
                {providerFilter.length === 1
                  ? `No ${providerEmptyVisits} for ${providerOptionLabel(providerFilter[0])}`
                  : `No ${providerEmptyVisits} for the ${providerFilter.length} selected providers`}
              </div>
            ) : (
              displayedData.map((x, index) =>
                !isCardView ? (
                  <CardView
                    key={x.id ?? index}
                    className="w-full sm:w-1/2 lg:w-1/3 xl:w-1/4"
                    data={x}
                    dob={dob.current}
                    insertdata={insertdata}
                    setFetchedData={setFetchedData}
                    alldata={fetchedData}
                    modalopen={openModal}
                    setIsStatusNotesPopUp={setIsStatusNotesPopUp}
                    fetchpcarddata={fetchpcarddata}
                    refreshDashboard={refreshDashboard}
                    setToastSeverity={setToastSeverity}
                    setToastMessage={setToastMessage}
                    setOpenToast={setOpenToast}
                    markAsComplete={markAsComplete}
                    markAscancel={markAscancel}
                    handleconfirmScheduleClick={handleconfirmScheduleClick}
                    scheduleStates={scheduleStates}
                    paymentdetails={validpayments}
                    selectedOrder={selectedOrder}
                    setSelectedOrder={setSelectedOrder}
                    setSelectedCard={setSelectedCard}
                    searchTerm={searchTerm}
                    setChat={setChat}
                    fetchedData={fetchedData}
                    handleschedular={handleschedular}
                  />
                ) : (
                  /* Keyed by row id, not index: with index keys the provider
                     filter made React reuse each position's card instance for
                     a DIFFERENT patient, and any state/memo inside kept the
                     old row (stale Chart handler, 2026-09-02). */
                  <ExpandedView
                    key={x.id ?? index}
                    dob={dob.current}
                    paymentdetails={validpayments}
                    insertdata={insertdata}
                    setFetchedData={setFetchedData}
                    alldata={fetchedData}
                    setIsStatusNotesPopUp={setIsStatusNotesPopUp}
                    fetchpcarddata={fetchpcarddata}
                    fetchedData={fetchedData}
                    setToastSeverity={setToastSeverity}
                    setToastMessage={setToastMessage}
                    setOpenToast={setOpenToast}
                    markAsComplete={markAsComplete}
                    markAscancel={markAscancel}
                    handleconfirmScheduleClick={handleconfirmScheduleClick}
                    scheduleStates={scheduleStates}
                    selectedOrder={selectedOrder}
                    setSelectedOrder={setSelectedOrder}
                    searchTerm={searchTerm}
                    modalopen={openModal}
                    fetchUpdateFrontDesk={fetchUpdateFrontDesk}
                    updateeyeicon={updateeyeicon}
                    rpmvalues={rpmvalues}
                    data={x}
                    setChat={setChat}
                    refreshDashboard={refreshDashboard}
                    setSelectedCard={setSelectedCard}
                    handleschedular={handleschedular}
                    // fetchDashboardData={fetchDashboardData}
                  />
                ),
              )
            )}
          </div>
        )}

        <DashboardFilterModal
          fetchDashboardData={fetchDashboardData}
          isOpen={filtermodal}
          onClose={() => {
            setFiltermodal(false);
          }}
          selectedFilterName={selectedFilterName}
          setSelectedFilterName={setSelectedFilterName}
          appliedFilter={appliedFilter}
          setAppliedFilter={setAppliedFilter}
        />
        <ImagingModal
          dob={dob.current || data.dob}
          data={data.current}
          setImagingModal={setImagingModal}
          imagingmodal={imagingmodal}
        />
        {/* <PcardPopup setPcard={setIsPcard} data={selectedCard} isPcard={isPcard} /> */}
        {isPcard ? (
          <PcardPopupNew
            refereshpcard={refereshpcard}
            setPcard={setIsPcard}
            dob={dob.current || data.dob}
            data={selectedCard}
            isPcard={isPcard}
          />
        ) : (
          <></>
        )}
        {isModalOpen ? (
          <ModalOverlay
            setFetchedData={setFetchedData}
            fetchedData={fetchedData}
            isloaderactive={isloaderactive}
            dob={dob.current || data.dob}
            modaldata={selectedCard}
            searchdate={searchdate}
            isOpen={isModalOpen}
            fetchpcarddata={fetchpcarddata}
            onClose={closeModal}
          />
        ) : (
          <></>
        )}
        <StatusNotesPopUp
          isStatusNotesPopUp={isStatusNotesPopUp}
          setIsStatusNotesPopUp={setIsStatusNotesPopUp}
        />
        {dashboardfilter && (
          <Dashfilter
            setDashboardfilter={setDashboardfilter}
            dashboardfilter={dashboardfilter}
          />
        )}
        {/*        
        {appointmentModal==1?<CreateAppointment
            isfutureappointment={true} 
                  
       
           setScreen={setAppointmentModal} 
              flow={'db'} //db=>dashboard
            selectedAppointment={futureappoinments[0]} 
             appointmentCardopen={true} 
                />
                :<></>} */}
        <ModalSwitch
          isfutureappointment={isfutureappointment}
          setIsFutureAppointment={setIsFutureAppointment}
          setScreen={setScreen}
          screen={screen}
          selectedDate={currentDate}
          flow="db"
          fetchScheduleData={() => {}}
          appointmentCardopen={appointmentCardopen}
          setIsappointmentCardOpen={setIsappointmentCardOpen}
          isAptcard={isAptCard}
          setIsAptcard={setIsAptCard}
          // selectedAppointment={selectedAppointment}
          selectedAppointment={{
            ...selectedAppointment,
            signintime: selectedAppointment?.signintime ?? null,
          }}
        />
        {chat ? (
          <ChatWrapper
            setChat={setChat}
            patientdata={selectedCard}
            flow={"comment"}
          />
        ) : (
          <></>
        )}
        <ToastMessage
          message={toastMessage}
          severity={toastSeverity}
          open={openToast}
          handleClose={handleToastClose}
        />
        <Backdrop open={isloader} sx={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </SideNavLayout>
    </div>
  );
};

export default CareLanding;
